import { Link, useLocation } from 'react-router-dom';

/** The trail on the right of a page header.
 *
 *  Derived from the route and the nav, not passed in per page. A hand-written
 *  breadcrumb is a second statement of where you are, and the second one is the
 *  one that goes stale when a route is renamed.
 *
 *  A `<nav>` with an ordered list, because that is what this is: the order
 *  carries the meaning, and a screen reader should be able to say "list, 3
 *  items" rather than read three unrelated links.
 */

type NavItem = { to: string; label: string };
type NavGroup = { section: string; items: NavItem[] };

export function Breadcrumbs({ nav }: { nav: NavGroup[] }) {
  const { pathname } = useLocation();

  if (pathname === '/') return null;

  const flat = nav.flatMap((group) =>
    group.items.map((item) => ({ ...item, section: group.section })),
  );

  // Longest match wins, so /emails/msg_123 resolves to the Email logs entry
  // rather than to Overview at '/'.
  const match = flat
    .filter((item) => item.to !== '/' && pathname.startsWith(item.to))
    .sort((a, b) => b.to.length - a.to.length)[0];

  if (!match) return null;

  // A detail page under a section: /emails/msg_123 gets a third crumb naming
  // the record, which is the only part of the trail that is not navigation.
  const rest = pathname.slice(match.to.length).replace(/^\//, '');

  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      <ol>
        <li>
          <Link to="/">{match.section}</Link>
        </li>
        <li>
          {rest ? <Link to={match.to}>{match.label}</Link> : <span aria-current="page">{match.label}</span>}
        </li>
        {rest && (
          <li>
            {/* Not linked: it is the page you are on, and a link to here is a
                link to nowhere. */}
            <span className="mono" aria-current="page">
              {rest.length > 24 ? `${rest.slice(0, 24)}…` : rest}
            </span>
          </li>
        )}
      </ol>
    </nav>
  );
}
